import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Shield, Mail, Lock, User, Phone, MapPin, Building2, 
  Loader2, Search, CheckCircle2, AlertCircle, Store, Calendar, XCircle,
  Eye, EyeOff
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import LegalDocModal from '@/components/LegalDocModal';
import PublicLegalFooter from '@/components/PublicLegalFooter';

// ==================== VALIDAÇÃO DE CPF ====================
const validateCPF = (cpf) => {
  cpf = cpf.replace(/\D/g, '');
  
  if (cpf.length !== 11) return false;
  
  // Bloquear CPFs inválidos conhecidos
  const invalidCPFs = [
    '00000000000', '11111111111', '22222222222', '33333333333',
    '44444444444', '55555555555', '66666666666', '77777777777',
    '88888888888', '99999999999', '12345678909'
  ];
  if (invalidCPFs.includes(cpf)) return false;
  
  // Calcular dígitos verificadores
  const calcDigit = (digits) => {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      sum += parseInt(digits[i]) * (digits.length + 1 - i);
    }
    const digit = 11 - (sum % 11);
    return digit > 9 ? 0 : digit;
  };
  
  return calcDigit(cpf.substring(0, 9)) === parseInt(cpf[9]) &&
         calcDigit(cpf.substring(0, 10)) === parseInt(cpf[10]);
};

// ==================== VALIDAÇÃO DE CNPJ ====================
const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/\D/g, '');
  
  if (cnpj.length !== 14) return false;
  
  // Bloquear CNPJs inválidos conhecidos
  const invalidCNPJs = [
    '00000000000000', '11111111111111', '22222222222222', '33333333333333',
    '44444444444444', '55555555555555', '66666666666666', '77777777777777',
    '88888888888888', '99999999999999'
  ];
  if (invalidCNPJs.includes(cnpj)) return false;
  
  // Calcular dígitos verificadores
  const calcDigit = (digits, weights) => {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      sum += parseInt(digits[i]) * weights[i];
    }
    const digit = 11 - (sum % 11);
    return digit > 9 ? 0 : digit;
  };
  
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  return calcDigit(cnpj.substring(0, 12), weights1) === parseInt(cnpj[12]) &&
         calcDigit(cnpj.substring(0, 13), weights2) === parseInt(cnpj[13]);
};

// ==================== VALIDAÇÃO DE SENHA FORTE ====================
const validatePasswordStrength = (password) => {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)
  };
  
  const isValid = Object.values(checks).every(v => v);
  
  let errorMessage = '';
  if (!checks.minLength) errorMessage = 'A senha deve ter no mínimo 8 caracteres.';
  else if (!checks.hasUppercase) errorMessage = 'A senha deve conter pelo menos 1 letra maiúscula.';
  else if (!checks.hasLowercase) errorMessage = 'A senha deve conter pelo menos 1 letra minúscula.';
  else if (!checks.hasNumber) errorMessage = 'A senha deve conter pelo menos 1 número.';
  else if (!checks.hasSpecial) errorMessage = 'A senha deve conter pelo menos 1 caractere especial (!@#$%...).';
  
  return { isValid, checks, errorMessage };
};

const DAYS_OF_WEEK = [
  { id: 1, name: 'Segunda-feira', short: 'Seg' },
  { id: 2, name: 'Terça-feira', short: 'Ter' },
  { id: 3, name: 'Quarta-feira', short: 'Qua' },
  { id: 4, name: 'Quinta-feira', short: 'Qui' },
  { id: 5, name: 'Sexta-feira', short: 'Sex' },
  { id: 6, name: 'Sábado', short: 'Sáb' },
  { id: 0, name: 'Domingo', short: 'Dom' },
];

export default function ProviderLogin() {
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepValid, setCepValid] = useState(null);
  const [docValid, setDocValid] = useState(null); // Estado para validação de documento
  const [passwordStrength, setPasswordStrength] = useState(null); // Estado para força da senha
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // LGPD — versões ativas (snapshot no momento do cadastro) + modais
  const [termsVersion, setTermsVersion] = useState('1.0');
  const [privacyVersion, setPrivacyVersion] = useState('1.0');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  // Estados do formulário
  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [registerStep, setRegisterStep] = useState(1); // 1: dados do lava-jato, 2: endereço e dias
  const [providerType, setProviderType] = useState('cpf'); // cpf ou cnpj
  const [registerForm, setRegisterForm] = useState({
    business_name: '', // Nome do lava-jato
    name: '', // Nome do responsável
    email: '',
    cpf_cnpj: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Endereço
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    // Dias de funcionamento
    working_days: [1, 2, 3, 4, 5] // Segunda a Sexta por padrão
  });

  // Redirect se já logado
  useEffect(() => {
    if (user && user.user_type === 'provider') {
      navigate('/provider/dashboard');
    }
  }, [user, navigate]);

  // LGPD — prefetch das versões ativas para registrar consentimento versionado
  useEffect(() => {
    const fetchActiveVersions = async () => {
      try {
        const [termsRes, privacyRes] = await Promise.all([
          axios.get(`${API}/admin/terms/active`, { params: { type: 'provider' } }).catch(() => null),
          axios.get(`${API}/admin/terms/active`, { params: { type: 'privacy_policy' } }).catch(() => null),
        ]);
        if (termsRes?.data?.version) setTermsVersion(termsRes.data.version);
        if (privacyRes?.data?.version) setPrivacyVersion(privacyRes.data.version);
      } catch (e) {
        // Mantém defaults "1.0"
      }
    };
    fetchActiveVersions();
  }, []);

  // Processar callback do Google OAuth — REMOVIDO
  // Decisão de produto (Fev/2026): prestadores não usam Google Auth.
  // Mantido apenas para clientes (ver AuthCallback.js).

  // Buscar dados do CEP
  const handleCepSearch = async () => {
    const cep = registerForm.cep.replace(/\D/g, '');
    
    if (cep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos');
      return;
    }
    
    setCepLoading(true);
    setCepValid(null);
    
    try {
      const res = await axios.get(`${API}/utils/validate-cep/${cep}`);
      
      setRegisterForm(prev => ({
        ...prev,
        street: res.data.street || prev.street,
        neighborhood: res.data.neighborhood || prev.neighborhood,
        city: res.data.city || prev.city,
        state: res.data.state || prev.state
      }));
      
      setCepValid(true);
      toast.success('CEP encontrado!');
    } catch (error) {
      setCepValid(false);
      toast.error('CEP não encontrado. Verifique e tente novamente.');
    } finally {
      setCepLoading(false);
    }
  };

  // Toggle dia de funcionamento
  const toggleWorkingDay = (dayId) => {
    setRegisterForm(prev => {
      const currentDays = prev.working_days || [];
      if (currentDays.includes(dayId)) {
        return { ...prev, working_days: currentDays.filter(d => d !== dayId) };
      } else {
        return { ...prev, working_days: [...currentDays, dayId] };
      }
    });
  };

  // Validar documento em tempo real
  const handleDocChange = (value) => {
    setRegisterForm({ ...registerForm, cpf_cnpj: value });
    const cleanDoc = value.replace(/\D/g, '');
    
    if (providerType === 'cpf') {
      if (cleanDoc.length === 11) {
        setDocValid(validateCPF(cleanDoc));
      } else if (cleanDoc.length > 0) {
        setDocValid(null);
      } else {
        setDocValid(null);
      }
    } else {
      if (cleanDoc.length === 14) {
        setDocValid(validateCNPJ(cleanDoc));
      } else if (cleanDoc.length > 0) {
        setDocValid(null);
      } else {
        setDocValid(null);
      }
    }
  };

  // Validar senha em tempo real
  const handlePasswordChange = (value) => {
    setRegisterForm({ ...registerForm, password: value });
    if (value.length > 0) {
      setPasswordStrength(validatePasswordStrength(value));
    } else {
      setPasswordStrength(null);
    }
  };

  // Reset validação de documento quando muda o tipo
  useEffect(() => {
    setDocValid(null);
    if (registerForm.cpf_cnpj) {
      const cleanDoc = registerForm.cpf_cnpj.replace(/\D/g, '');
      if (providerType === 'cpf' && cleanDoc.length === 11) {
        setDocValid(validateCPF(cleanDoc));
      } else if (providerType === 'cnpj' && cleanDoc.length === 14) {
        setDocValid(validateCNPJ(cleanDoc));
      }
    }
  }, [providerType]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/login`, {
        identifier: loginForm.identifier,
        password: loginForm.password
      });
      
      if (res.data.user.user_type !== 'provider') {
        toast.error('Esta área é exclusiva para prestadores');
        return;
      }
      
      login(res.data.token, res.data.user);
      toast.success('Login realizado!');
      navigate('/provider/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterStep1 = (e) => {
    e.preventDefault();
    
    // Validação do nome do lava-jato
    if (!registerForm.business_name || registerForm.business_name.length < 3) {
      toast.error('Nome do lava-jato deve ter pelo menos 3 caracteres');
      return;
    }
    
    // Validação do nome do responsável
    if (!registerForm.name || registerForm.name.length < 3) {
      toast.error('Nome do responsável deve ter pelo menos 3 caracteres');
      return;
    }
    
    // Validação de e-mail
    if (!registerForm.email || !registerForm.email.includes('@')) {
      toast.error('E-mail inválido');
      return;
    }
    
    // VALIDAÇÃO RIGOROSA DE CPF/CNPJ
    if (!registerForm.cpf_cnpj) {
      toast.error(providerType === 'cpf' ? 'Informe o CPF' : 'Informe o CNPJ');
      return;
    }
    
    const cleanDoc = registerForm.cpf_cnpj.replace(/\D/g, '');
    
    if (providerType === 'cpf') {
      if (cleanDoc.length !== 11) {
        toast.error('CPF deve ter 11 dígitos');
        return;
      }
      if (!validateCPF(cleanDoc)) {
        toast.error('CPF inválido. Verifique os dígitos e tente novamente.');
        return;
      }
    } else {
      if (cleanDoc.length !== 14) {
        toast.error('CNPJ deve ter 14 dígitos');
        return;
      }
      if (!validateCNPJ(cleanDoc)) {
        toast.error('CNPJ inválido. Verifique os dígitos e tente novamente.');
        return;
      }
    }
    
    // Validação de telefone
    if (!registerForm.phone || registerForm.phone.replace(/\D/g, '').length < 10) {
      toast.error('Telefone deve ter pelo menos 10 dígitos (com DDD)');
      return;
    }
    
    // Validação de confirmação de senha
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    
    // VALIDAÇÃO RIGOROSA DE SENHA FORTE
    const pwdValidation = validatePasswordStrength(registerForm.password);
    if (!pwdValidation.isValid) {
      toast.error(pwdValidation.errorMessage);
      return;
    }

    // Aceite dos Termos de Uso é obrigatório
    if (!termsAccepted) {
      toast.error('Você precisa aceitar os Termos do Prestador e a Política de Privacidade.');
      return;
    }

    // Ir para etapa 2
    setRegisterStep(2);
  };

  const handleRegisterStep2 = async (e) => {
    e.preventDefault();
    
    // Validações de endereço
    if (!registerForm.cep || registerForm.cep.replace(/\D/g, '').length !== 8) {
      toast.error('CEP inválido');
      return;
    }
    
    if (!registerForm.street) {
      toast.error('Informe a rua/logradouro');
      return;
    }
    
    if (!registerForm.number) {
      toast.error('Informe o número');
      return;
    }
    
    if (!registerForm.neighborhood) {
      toast.error('Informe o bairro');
      return;
    }
    
    if (!registerForm.city) {
      toast.error('Informe a cidade');
      return;
    }
    
    if (!registerForm.state) {
      toast.error('Informe o estado');
      return;
    }
    
    // Validação de dias de funcionamento
    if (!registerForm.working_days || registerForm.working_days.length === 0) {
      toast.error('Selecione pelo menos um dia de funcionamento');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register/provider`, {
        business_name: registerForm.business_name,
        name: registerForm.name,
        email: registerForm.email,
        cpf_cnpj: registerForm.cpf_cnpj.replace(/\D/g, ''),
        phone: registerForm.phone.replace(/\D/g, ''),
        password: registerForm.password,
        address: {
          cep: registerForm.cep.replace(/\D/g, ''),
          street: registerForm.street,
          number: registerForm.number,
          complement: registerForm.complement || null,
          neighborhood: registerForm.neighborhood,
          city: registerForm.city,
          state: registerForm.state
        },
        working_days: registerForm.working_days,
        terms_accepted: termsAccepted,
        accepted_terms_version: termsVersion,
        accepted_privacy_version: privacyVersion
      });
      
      login(res.data.token, res.data.user);
      toast.success('Cadastro realizado com sucesso!');
      navigate('/provider/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google Auth não suportado para prestadores (decisão de produto Fev/2026).
    toast.error('Cadastro/login com Google não está disponível para prestadores.');
  };

  // googleLoading splash REMOVIDO — Google Auth não está mais disponível para prestadores.

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-lg p-8 border-[#E2E8F0] shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div className="bg-[#0B1F3A] p-3 rounded-xl">
            <Shield className="w-10 h-10 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-center mb-1 text-[#0B1F3A]">JATOO</h1>
        <p className="text-center text-slate-500 mb-6">Área do Prestador (Lava-Jato)</p>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100">
            <TabsTrigger value="login" data-testid="tab-login" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Entrar</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register" onClick={() => setRegisterStep(1)} className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Cadastrar</TabsTrigger>
          </TabsList>

          {/* TAB LOGIN */}
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-identifier">E-mail, CPF ou CNPJ</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="login-identifier"
                    placeholder="email@exemplo.com ou 000.000.000-00"
                    className="pl-10"
                    value={loginForm.identifier}
                    onChange={(e) => setLoginForm({ ...loginForm, identifier: e.target.value })}
                    required
                    data-testid="login-identifier-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="login-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Entrar
              </Button>
            </form>
          </TabsContent>

          {/* TAB CADASTRO */}
          <TabsContent value="register">
            {/* Etapa 1: Dados do Lava-Jato */}
            {registerStep === 1 && (
              <form onSubmit={handleRegisterStep1} className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 h-2 bg-primary rounded-full"></div>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full"></div>
                </div>
                <p className="text-sm text-muted-foreground text-center mb-4">Etapa 1 de 2: Dados do Lava-Jato</p>

                <div>
                  <Label>Tipo de Cadastro</Label>
                  <Select value={providerType} onValueChange={setProviderType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpf">Pessoa Física (CPF)</SelectItem>
                      <SelectItem value="cnpj">Pessoa Jurídica (CNPJ)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Limite diário: {providerType === 'cpf' ? '15' : '30'} atendimentos
                  </p>
                </div>

                {/* Nome do Lava-Jato */}
                <div>
                  <Label htmlFor="register-business-name">
                    <Store className="inline w-4 h-4 mr-1" />
                    Nome do Lava-Jato *
                  </Label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-business-name"
                      placeholder="Ex: Auto Lavagem Express"
                      className="pl-10"
                      value={registerForm.business_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, business_name: e.target.value })}
                      required
                      data-testid="register-business-name-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Este nome será exibido para os clientes
                  </p>
                </div>

                {/* Nome do Responsável */}
                <div>
                  <Label htmlFor="register-name">
                    <User className="inline w-4 h-4 mr-1" />
                    Nome do Responsável *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-name"
                      placeholder="Nome completo do proprietário"
                      className="pl-10"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Uso administrativo (não exibido para clientes)
                  </p>
                </div>

                <div>
                  <Label htmlFor="register-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="email@exemplo.com"
                      className="pl-10"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-cpf">{providerType === 'cpf' ? 'CPF *' : 'CNPJ *'}</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-cpf"
                      placeholder={providerType === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                      className={`pl-10 pr-10 ${docValid === true ? 'border-green-500 focus:border-green-500' : docValid === false ? 'border-red-500 focus:border-red-500' : ''}`}
                      value={registerForm.cpf_cnpj}
                      onChange={(e) => handleDocChange(e.target.value)}
                      required
                      data-testid="register-cpf-input"
                    />
                    {docValid === true && (
                      <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    )}
                    {docValid === false && (
                      <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                    )}
                  </div>
                  {docValid === false && (
                    <p className="text-xs text-red-500 mt-1">
                      {providerType === 'cpf' ? 'CPF inválido. Verifique os dígitos.' : 'CNPJ inválido. Verifique os dígitos.'}
                    </p>
                  )}
                  {docValid === true && (
                    <p className="text-xs text-green-500 mt-1">
                      {providerType === 'cpf' ? 'CPF válido!' : 'CNPJ válido!'}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="register-phone">Telefone (com DDD)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="register-phone"
                      placeholder="(11) 99999-9999"
                      className="pl-10"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      required
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="register-password">Senha *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Senha forte"
                        className={`pl-10 pr-10 ${passwordStrength?.isValid === true ? 'border-green-500' : passwordStrength?.isValid === false ? 'border-red-500' : ''}`}
                        value={registerForm.password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        required
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-confirm">Confirmar Senha *</Label>
                    <div className="relative">
                      <Input
                        id="register-confirm"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Repita a senha"
                        className={`pr-10 ${registerForm.confirmPassword && registerForm.password !== registerForm.confirmPassword ? 'border-red-500' : registerForm.confirmPassword && registerForm.password === registerForm.confirmPassword ? 'border-green-500' : ''}`}
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        required
                        data-testid="register-confirm-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Indicador de força da senha */}
                {passwordStrength && (
                  <div className="space-y-2 p-3 bg-slate-50 rounded-lg border">
                    <p className="text-xs font-medium text-slate-700">Requisitos da senha:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className={`flex items-center gap-1 ${passwordStrength.checks.minLength ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordStrength.checks.minLength ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        Mínimo 8 caracteres
                      </div>
                      <div className={`flex items-center gap-1 ${passwordStrength.checks.hasUppercase ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordStrength.checks.hasUppercase ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        Letra maiúscula
                      </div>
                      <div className={`flex items-center gap-1 ${passwordStrength.checks.hasLowercase ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordStrength.checks.hasLowercase ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        Letra minúscula
                      </div>
                      <div className={`flex items-center gap-1 ${passwordStrength.checks.hasNumber ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordStrength.checks.hasNumber ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        Número
                      </div>
                      <div className={`flex items-center gap-1 col-span-2 ${passwordStrength.checks.hasSpecial ? 'text-green-600' : 'text-red-500'}`}>
                        {passwordStrength.checks.hasSpecial ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        Caractere especial (!@#$%...)
                      </div>
                    </div>
                  </div>
                )}

                {/* Checkbox de Aceite dos Termos do Prestador + Política de Privacidade (LGPD) */}
                <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  <Checkbox
                    id="provider-accept-terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    data-testid="provider-register-terms-checkbox"
                  />
                  <label
                    htmlFor="provider-accept-terms"
                    className="text-sm leading-relaxed cursor-pointer text-slate-700"
                  >
                    <span>Li e aceito os </span>
                    <button
                      type="button"
                      className="text-primary hover:underline font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTermsModal(true);
                      }}
                      data-testid="provider-terms-link"
                    >
                      Termos do Prestador
                    </button>
                    <span> e a </span>
                    <button
                      type="button"
                      className="text-primary hover:underline font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPrivacyModal(true);
                      }}
                      data-testid="provider-privacy-link"
                    >
                      Política de Privacidade
                    </button>
                    <span> da JATOO.</span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={!termsAccepted}
                  data-testid="register-next-btn"
                >
                  Próximo: Endereço e Funcionamento
                </Button>
              </form>
            )}

            {/* Etapa 2: Endereço e Dias de Funcionamento */}
            {registerStep === 2 && (
              <form onSubmit={handleRegisterStep2} className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 h-2 bg-primary rounded-full"></div>
                  <div className="flex-1 h-2 bg-primary rounded-full"></div>
                </div>
                <p className="text-sm text-muted-foreground text-center mb-4">Etapa 2 de 2: Endereço e Funcionamento</p>

                <Alert className="bg-blue-50 border-blue-200">
                  <MapPin className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    Informe o endereço onde os clientes irão deixar os veículos.
                  </AlertDescription>
                </Alert>

                <div>
                  <Label htmlFor="register-cep">CEP</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-cep"
                        placeholder="00000-000"
                        className="pl-10"
                        value={registerForm.cep}
                        onChange={(e) => {
                          setRegisterForm({ ...registerForm, cep: e.target.value });
                          setCepValid(null);
                        }}
                        required
                        data-testid="register-cep-input"
                      />
                    </div>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleCepSearch}
                      disabled={cepLoading}
                      data-testid="cep-search-btn"
                    >
                      {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  {cepValid === true && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> CEP válido
                    </p>
                  )}
                  {cepValid === false && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> CEP não encontrado
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="register-street">Rua / Logradouro</Label>
                  <Input
                    id="register-street"
                    placeholder="Av. Paulista"
                    value={registerForm.street}
                    onChange={(e) => setRegisterForm({ ...registerForm, street: e.target.value })}
                    required
                    data-testid="register-street-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="register-number">Número</Label>
                    <Input
                      id="register-number"
                      placeholder="1000"
                      value={registerForm.number}
                      onChange={(e) => setRegisterForm({ ...registerForm, number: e.target.value })}
                      required
                      data-testid="register-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-complement">Complemento</Label>
                    <Input
                      id="register-complement"
                      placeholder="Sala 01 (opcional)"
                      value={registerForm.complement}
                      onChange={(e) => setRegisterForm({ ...registerForm, complement: e.target.value })}
                      data-testid="register-complement-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="register-neighborhood">Bairro</Label>
                  <Input
                    id="register-neighborhood"
                    placeholder="Centro"
                    value={registerForm.neighborhood}
                    onChange={(e) => setRegisterForm({ ...registerForm, neighborhood: e.target.value })}
                    required
                    data-testid="register-neighborhood-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="register-city">Cidade</Label>
                    <Input
                      id="register-city"
                      placeholder="São Paulo"
                      value={registerForm.city}
                      onChange={(e) => setRegisterForm({ ...registerForm, city: e.target.value })}
                      required
                      data-testid="register-city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-state">UF</Label>
                    <Input
                      id="register-state"
                      placeholder="SP"
                      maxLength={2}
                      value={registerForm.state}
                      onChange={(e) => setRegisterForm({ ...registerForm, state: e.target.value.toUpperCase() })}
                      required
                      data-testid="register-state-input"
                    />
                  </div>
                </div>

                {/* Dias de Funcionamento */}
                <div className="space-y-3 pt-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Dias de Funcionamento *
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Selecione os dias em que o lava-jato funciona
                  </p>
                  
                  <div className="grid grid-cols-4 gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <div
                        key={day.id}
                        className={`
                          flex items-center justify-center p-2 rounded-lg border-2 cursor-pointer transition-all
                          ${registerForm.working_days.includes(day.id) 
                            ? 'bg-primary text-primary-foreground border-primary' 
                            : 'bg-white border-gray-200 hover:border-primary/50'}
                        `}
                        onClick={() => toggleWorkingDay(day.id)}
                        data-testid={`working-day-${day.id}`}
                      >
                        <div className="text-center">
                          <span className="text-sm font-medium">{day.short}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {registerForm.working_days.length === 0 && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Selecione pelo menos um dia
                    </p>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    Horário padrão: 08:00 às 18:00 (você poderá ajustar depois nas configurações)
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setRegisterStep(1)}
                    data-testid="register-back-btn"
                  >
                    Voltar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1" 
                    disabled={loading || registerForm.working_days.length === 0}
                    data-testid="register-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Finalizar Cadastro
                  </Button>
                </div>
              </form>
            )}

            {registerStep === 1 && (
              <>
                {/* Botão "Cadastrar com Google" REMOVIDO (Fev/2026):
                    prestadores só usam cadastro tradicional. */}
              </>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Button variant="link" onClick={() => navigate('/')} className="text-muted-foreground">
            Voltar ao início
          </Button>
        </div>
      </Card>

      {/* LGPD — Modais de Termos do Prestador e Política de Privacidade */}
      <LegalDocModal
        type="provider"
        open={showTermsModal}
        onOpenChange={setShowTermsModal}
        testIdPrefix="provider-terms"
      />
      <LegalDocModal
        type="privacy_policy"
        open={showPrivacyModal}
        onOpenChange={setShowPrivacyModal}
        testIdPrefix="provider-privacy"
      />

      {/* Rodapé público de compliance jurídico */}
      <div className="w-full max-w-lg mt-6">
        <PublicLegalFooter />
      </div>
    </div>
  );
}
